import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Card,
    Select,
    Button,
    Table,
    Checkbox,
    Tag,
    Modal,
    Tooltip,
    Alert,
    InputNumber,
    Space,
    Typography,
    Divider,
    Empty,
    Popconfirm,
    Badge,
    Input,
} from 'antd';
import {
    SaveOutlined,
    TeamOutlined,
    HistoryOutlined,
    DownloadOutlined,
    PrinterOutlined,
    ReloadOutlined,
    ExclamationCircleOutlined,
    LockOutlined,
    UserOutlined,
    SwapOutlined,
    SearchOutlined,
    CalendarOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest, postRequest, patchRequest } from 'api';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import { ClassLevelSelect } from 'Components';
import './ClassIncomeBoard.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Interfaces matching Backend ClassIncomeBoardDTO
interface IncomeSourceColumn {
    id: number;
    code: string;
    name: string;
    collectBy: number;
    isEinvoice: boolean;
    isOptional: boolean;
}

interface StudentIncomeItem {
    id: number;
    incomeSchoolId: number;
    incomeSchoolCode: string;
    incomeSchoolName: string;
    collectBy: number;
    unitType: number;
    expectedUnits: number;
    unitPrice: number;
    amount: number;
    hasInvoice: boolean;
    actualUnits: number;
    carryUnits: number;
    carryAmount: number;
    mustCollectUnits?: number;
    mustCollectAmount?: number;
    invoiceStatus?: number | null;
    invoiceId?: string | null;
    description?: string;
    isPaidAnnual?: boolean;
    paidAnnualNote?: string;
}

interface StudentDetailRow {
    studentClassYearId: number;
    incomeSchoolId: number;
    code: string;
    name: string;
    collectBy: number;
    isRegistered: boolean;
    isLocked: boolean;
    id?: number;
    carryUnits?: number | null;
    carryAmount?: number | null;
    expectedUnits?: number | null;
    amount?: number | null;
    discountRegularUnits?: number | null;
    discountRegularAmount?: number | null;
    discountMonthUnits?: number | null;
    discountMonthAmount?: number | null;
    mustCollectUnits?: number | null;
    mustCollectAmount?: number | null;
    unitPrice?: number | null;
    hasInvoice?: boolean;
    invoiceStatus?: number | null;
    invoiceId?: string | null;
    isPaidAnnual?: boolean;
    paidAnnualNote?: string;
}

interface StudentRow {
    studentClassYearId: number;
    studentId: number;
    studentCode: string;
    fullName: string;
    dob?: string;
    gender?: number;
    revenueBlockId?: number;
    revenueBlockName?: string;
    isSelfRegister: boolean;
    selfRegister?: boolean;
    registeredDate?: string;
    registeredBy?: string;
    registeredIncomeIds: number[];
    lockedIncomeIds: number[];
    items: StudentIncomeItem[];
}

interface BlockDefaultItem {
    units: number;
    unitPrice: number;
    amount: number;
    isDefault?: boolean;
}

interface ClassIncomeBoardData {
    classYearId: number;
    className: string;
    schoolYear: string;
    classLevel: string;
    month: number;
    year: number;
    isPastSchoolYear: boolean;
    isMonthLocked?: boolean;
    totalStudents: number;
    unassignedBlockCount: number;
    lastRegisteredDate?: string | null;
    columns: IncomeSourceColumn[];
    students: StudentRow[];
    blockDefaults?: Record<number, Record<number, BlockDefaultItem>>;
}

interface RevenueBlockOption {
    id: number;
    code: string;
    name: string;
}

interface ClassYearOption {
    classYearId: number;
    className: string;
    schoolYear: string;
    classLevel: string;
}

const COLLECT_BY_LABEL: Record<number, { text: string; color: string }> = {
    1: { text: 'Tháng', color: 'blue' },
    2: { text: 'Năm', color: 'purple' },
    3: { text: 'Suất-tháng', color: 'orange' },
    4: { text: 'Kỳ', color: 'cyan' },
};

const COLLECT_BY_DETAIL_LABEL: Record<number, { text: string; color: string }> = {
    1: { text: 'Theo tháng', color: 'blue' },
    2: { text: 'Theo năm', color: 'purple' },
    3: { text: 'Suất-tháng', color: 'orange' },
    4: { text: 'Theo kỳ', color: 'cyan' },
};

export const ClassIncomeBoard: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filters
    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(() => dayjs(`${getStoredWorkMonth().year}`, 'YYYY'));
    const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
    const [selectedClassYearId, setSelectedClassYearId] = useState<number | null>(null);
    const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);

    // Lắng nghe thay đổi kỳ làm việc từ cấu hình
    useEffect(() => {
        const handleWorkMonthChange = (e: any) => {
            if (e?.detail) {
                setSelectedMonth(e.detail.month);
                setSelectedYear(dayjs(`${e.detail.year}`, 'YYYY'));
            }
        };
        window.addEventListener('workMonthChanged', handleWorkMonthChange);
        return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
    }, []);

    // Data lists for filters
    const [classList, setClassList] = useState<ClassYearOption[]>([]);
    const [revenueBlocks, setRevenueBlocks] = useState<RevenueBlockOption[]>([]);

    // Board Data & Matrix state
    const [loading, setLoading] = useState<boolean>(false);
    const [boardData, setBoardData] = useState<ClassIncomeBoardData | null>(null);
    // Local matrix: studentClassYearId -> Set<incomeSchoolId>
    const [localMatrix, setLocalMatrix] = useState<Record<number, Set<number>>>({});
    // Local self-register: studentClassYearId -> boolean (saved on [Lập đăng ký])
    const [localSelfRegister, setLocalSelfRegister] = useState<Record<number, boolean>>({});
    const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

    // Saving & batch assign states
    const [saving, setSaving] = useState<boolean>(false);
    const [showAssignBlockModal, setShowAssignBlockModal] = useState<boolean>(false);
    const [targetBlockId, setTargetBlockId] = useState<number | null>(null);
    const [assignTargetType, setAssignTargetType] = useState<'all' | 'unassigned' | 'selected'>('all');
    const [assigningBlock, setAssigningBlock] = useState<boolean>(false);

    // Multi-row selection for quick batch block switching
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [batchBlockId, setBatchBlockId] = useState<number | null>(null);
    const [batchSwitchingBlock, setBatchSwitchingBlock] = useState<boolean>(false);

    // Filters for Zone 2 (Client-side with 300ms debounce)
    const [searchInput, setSearchInput] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [filterBlockId, setFilterBlockId] = useState<number | 'unassigned' | 'all'>('all');

    // Debounce search input by 300ms
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Double-click inline cell edit states for Zone 3 (Temporary calculation on FE, saved on [Lập đăng ký])
    const [editingCell, setEditingCell] = useState<{ incomeSchoolId: number; field: 'units' | 'amount' } | null>(null);
    const [cellTempValue, setCellTempValue] = useState<number>(0);
    const [savingCell, setSavingCell] = useState<boolean>(false);
    const [localItemEdits, setLocalItemEdits] = useState<
        Record<number, Record<number, { expectedUnits: number; unitPrice: number; amount: number }>>
    >({});

    // Invoice generation & cancellation states
    const [generatingInvoices, setGeneratingInvoices] = useState<boolean>(false);
    const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState<boolean>(false);
    const [cancelingInvoice, setCancelingInvoice] = useState<boolean>(false);

    // Checkbox "Hiện tất cả nguồn thu" ở Bảng 2 (giống màn Theo dõi thu nộp)
    const [showAllIncomes, setShowAllIncomes] = useState<boolean>(true);

    // 1. Load initial Revenue Blocks (Work month loaded from localStorage)
    useEffect(() => {
        if (!schoolId) return;

        // Fetch revenue blocks
        getRequest(`/cms/accountant/revenue-blocks?schoolId=${schoolId}`).then((res: any) => {
            if (res?.code === 200 && Array.isArray(res.info)) {
                setRevenueBlocks(res.info);
            }
        }).catch(() => { });
    }, [schoolId]);

    // 2. Load classes for the selected school year
    const fetchClasses = useCallback(async (year: number) => {
        if (!schoolId) return;
        try {
            const res: any = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${year}&schoolId=${schoolId}&page=1&perPage=200&sort=asc&sortBy=className`
            );
            if (res?.code === 200 && res.info?.list) {
                setClassList(res.info.list);
                // Check if classYearId in search params exists
                const paramClassId = searchParams.get('classYearId');
                if (paramClassId) {
                    const parsed = parseInt(paramClassId, 10);
                    if (res.info.list.some((c: ClassYearOption) => c.classYearId === parsed)) {
                        setSelectedClassYearId(parsed);
                        return;
                    }
                }
                // Default to first class if not selected
                if (res.info.list.length > 0 && !selectedClassYearId) {
                    setSelectedClassYearId(res.info.list[0].classYearId);
                }
            } else {
                setClassList([]);
            }
        } catch {
            setClassList([]);
        }
    }, [schoolId, searchParams, selectedClassYearId]);

    useEffect(() => {
        if (selectedYear) {
            fetchClasses(selectedYear.year());
        }
    }, [selectedYear, fetchClasses]);

    // 3. Load Board Data
    const fetchBoardData = useCallback(async () => {
        if (!selectedClassYearId || !selectedYear) return;
        setLoading(true);
        try {
            const yearNum = selectedYear.year();
            const res: any = await getRequest(
                `/cms/accountant/class-income/${selectedClassYearId}?month=${selectedMonth}&year=${yearNum}`
            );
            if (res?.code === 200 && res.info) {
                const data: ClassIncomeBoardData = res.info;
                setBoardData(data);

                // Initialize local matrix and local self-register from server data
                const matrix: Record<number, Set<number>> = {};
                const selfRegMap: Record<number, boolean> = {};
                data.students.forEach((s) => {
                    matrix[s.studentClassYearId] = new Set(s.registeredIncomeIds || []);
                    selfRegMap[s.studentClassYearId] = Boolean(s.isSelfRegister ?? (s as any).selfRegister);
                });
                setLocalMatrix(matrix);
                setLocalSelfRegister(selfRegMap);
                setLocalItemEdits({});

                // Auto-select first student if none selected or not in current list
                if (data.students.length > 0) {
                    if (!selectedStudentId || !data.students.some((s) => s.studentClassYearId === selectedStudentId)) {
                        setSelectedStudentId(data.students[0].studentClassYearId);
                    }
                } else {
                    setSelectedStudentId(null);
                }
            } else {
                toast.error(res?.msg || 'Không thể tải dữ liệu bảng nguồn thu');
                setBoardData(null);
            }
        } catch (err: any) {
            toast.error('Lỗi khi tải bảng nguồn thu: ' + (err?.message || 'Lỗi mạng'));
            setBoardData(null);
        } finally {
            setLoading(false);
        }
    }, [selectedClassYearId, selectedMonth, selectedYear, selectedStudentId]);

    useEffect(() => {
        setSelectedRowKeys([]);
        setSearchInput('');
        setDebouncedSearch('');
        setFilterBlockId('all');
        fetchBoardData();
    }, [selectedClassYearId, selectedMonth, selectedYear]);

    // Update query param when class changes
    const handleClassChange = (classYearId: number) => {
        setSelectedClassYearId(classYearId);
        setSearchParams({ classYearId: classYearId.toString(), month: selectedMonth.toString() });
    };

    const filteredClassList = useMemo(() => {
        if (!selectedClassLevel) return classList;
        return classList.filter((c) => String(c.classLevel).trim() === selectedClassLevel.trim());
    }, [classList, selectedClassLevel]);

    const handleClassLevelChange = (level?: string) => {
        setSelectedClassLevel(level);
        const nextList = level
            ? classList.filter((c) => String(c.classLevel).trim() === level.trim())
            : classList;
        if (nextList.length > 0) {
            if (!nextList.some((c) => c.classYearId === selectedClassYearId)) {
                handleClassChange(nextList[0].classYearId);
            }
        } else {
            setSelectedClassYearId(null);
            setBoardData(null);
        }
    };

    // Matrix cell toggle
    const handleToggleCell = (scyId: number, incomeId: number, locked: boolean) => {
        setSelectedStudentId(scyId);
        if (boardData?.isPastSchoolYear) {
            toast.warn('Năm học đã kết thúc, không thể thay đổi đăng ký!');
            return;
        }
        if (locked) {
            toast.warn('Khoản thu này đã xuất biên lai, không thể hủy bỏ!');
            return;
        }

        setLocalMatrix((prev) => {
            const currentSet = new Set(prev[scyId] || []);
            if (currentSet.has(incomeId)) {
                currentSet.delete(incomeId);
            } else {
                currentSet.add(incomeId);
            }
            return { ...prev, [scyId]: currentSet };
        });
    };

    // Header Select All for a column
    const handleToggleColumnAll = (incomeId: number, targetChecked: boolean) => {
        if (boardData?.isPastSchoolYear) {
            toast.warn('Năm học đã kết thúc, không thể sửa!');
            return;
        }

        setLocalMatrix((prev) => {
            const updated = { ...prev };
            boardData?.students.forEach((s) => {
                const isLocked = s.lockedIncomeIds?.includes(incomeId);
                if (isLocked) return; // Keep locked ones untouched

                const set = new Set(updated[s.studentClassYearId] || []);
                if (targetChecked) {
                    set.add(incomeId);
                } else {
                    set.delete(incomeId);
                }
                updated[s.studentClassYearId] = set;
            });
            return updated;
        });
    };

    // Matrix cell toggle for self-register (Only updates local state, saved on [Lập đăng ký])
    const handleToggleSelfRegister = (scyId: number, checked: boolean) => {
        setSelectedStudentId(scyId);
        if (boardData?.isPastSchoolYear) {
            toast.warn('Năm học đã kết thúc, không thể thay đổi!');
            return;
        }
        setLocalSelfRegister((prev) => ({
            ...prev,
            [scyId]: checked,
        }));
        if (!checked) {
            setLocalItemEdits((prev) => {
                if (!prev[scyId]) return prev;
                const next = { ...prev };
                delete next[scyId];
                return next;
            });
        }
    };

    // Toggle self-register flag for all students in the class (Local state)
    const handleToggleAllSelfRegister = (checked: boolean) => {
        if (boardData?.isPastSchoolYear || !boardData) {
            toast.warn('Năm học đã kết thúc, không thể thay đổi!');
            return;
        }
        setLocalSelfRegister((prev) => {
            const updated = { ...prev };
            boardData.students.forEach((s) => {
                updated[s.studentClassYearId] = checked;
            });
            return updated;
        });
        if (!checked) {
            setLocalItemEdits({});
        }
    };

    // Single student revenue block change
    const handleChangeSingleRevenueBlock = async (scyId: number, rbId: number) => {
        if (boardData?.isPastSchoolYear) return;

        const student = boardData?.students.find((s) => s.studentClassYearId === scyId);
        if (!student) return;

        // Kiểm tra biên lai chính thức trong tháng hiện tại (status 0: Chưa thanh toán, status 1: Đã thanh toán, loại trừ khoản thu năm đã nộp ở tháng khác)
        const officialItems = (student.items || []).filter(
            (it) => !it.isPaidAnnual && it.hasInvoice && (it.invoiceStatus === 0 || it.invoiceStatus === 1)
        );

        // Kiểm tra biên lai Chờ duyệt (status -1)
        const draftItems = (student.items || []).filter(
            (it) => !it.isPaidAnnual && it.hasInvoice && it.invoiceStatus === -1
        );

        const executeChangeBlock = async () => {
            try {
                const res: any = await patchRequest('/cms/accountant/class-income/revenue-block', {
                    studentClassYearIds: [scyId],
                    revenueBlockId: rbId,
                    month: selectedMonth,
                    year: selectedYear?.year(),
                });
                if (res?.code === 200) {
                    toast.success('Đã cập nhật khối thu cho học sinh. Vui lòng bấm [Lập đăng ký] để xác nhận lại nguồn thu!');
                    await fetchBoardData();
                } else {
                    toast.error(res?.msg || 'Cập nhật thất bại');
                }
            } catch {
                toast.error('Lỗi khi cập nhật khối thu');
            }
        };

        if (officialItems.length > 0 || draftItems.length > 0) {
            const targetBlockName = revenueBlocks.find((b) => b.id === rbId)?.name || 'Khối thu mới';
            Modal.confirm({
                title: 'Xác nhận chuyển khối thu cho học sinh',
                icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                content: (
                    <div style={{ fontSize: 13, lineHeight: '20px' }}>
                        {officialItems.length > 0 && (
                            <p style={{ marginBottom: 8 }}>
                                Học sinh <b>{student.fullName}</b> hiện có{' '}
                                <b style={{ color: '#1677ff' }}>{officialItems.length}</b> khoản thu đã phát hành biên lai hoặc đã thanh toán trong tháng. Các khoản thu này sẽ được <b>giữ nguyên</b> để đảm bảo an toàn chứng từ.
                            </p>
                        )}
                        {draftItems.length > 0 && (
                            <p style={{ marginBottom: 8 }}>
                                Học sinh hiện có <b style={{ color: '#fa8c16' }}>{draftItems.length}</b> khoản thu ở trạng thái <b>Chờ duyệt (nháp)</b>. Các biên lai nháp này sẽ tự động bị hủy để cập nhật lại theo khối mới.
                            </p>
                        )}
                        <p style={{ marginBottom: 8 }}>
                            Các khoản thu chưa xuất biên lai sẽ được đồng bộ theo mức giá của khối <b>{targetBlockName}</b>. Sau khi chuyển khối, bạn cần bấm <b>[Lập đăng ký]</b> để xác nhận lại.
                        </p>
                        <p style={{ marginBottom: 0, fontWeight: 600, color: '#f5222d' }}>
                            Bạn có chắc chắn muốn chuyển khối thu cho học sinh này không?
                        </p>
                    </div>
                ),
                okText: 'Đồng ý chuyển khối',
                okType: 'primary',
                okButtonProps: { danger: true },
                cancelText: 'Hủy',
                onOk: executeChangeBlock,
            });
            return;
        }

        await executeChangeBlock();
    };

    // Batch register submit [Lập đăng ký]
    const handleBatchRegister = async () => {
        if (!selectedClassYearId || !selectedYear || !boardData) return;
        if (boardData.isPastSchoolYear) {
            toast.warn('Năm học đã kết thúc, không thể lập đăng ký!');
            return;
        }

        const payloadStudents = boardData.students.map((s) => {
            const scyId = s.studentClassYearId;
            const isSelf = Boolean(localSelfRegister[scyId]);
            const virtualPaidAnnualIds = new Set(
                (s.items || [])
                    .filter((it) => it.isPaidAnnual && (!it.id || it.id === 0))
                    .map((it) => it.incomeSchoolId)
            );
            const checkedIds = Array.from(localMatrix[scyId] || []).filter(
                (incId) => !virtualPaidAnnualIds.has(incId)
            );
            const studentEdits = isSelf ? (localItemEdits[scyId] || {}) : {};
            const customItems = checkedIds
                .filter((incId) => studentEdits[incId] != null)
                .map((incId) => ({
                    incomeSchoolId: incId,
                    expectedUnits: studentEdits[incId].expectedUnits,
                    unitPrice: studentEdits[incId].unitPrice,
                }));

            return {
                studentClassYearId: scyId,
                incomeSchoolIds: checkedIds,
                isSelfRegister: isSelf,
                customItems,
            };
        });

        setSaving(true);
        try {
            const res: any = await postRequest(`/cms/accountant/class-income/${selectedClassYearId}/register`, {
                month: selectedMonth,
                year: selectedYear.year(),
                students: payloadStudents,
            });

            if (res?.code === 200) {
                toast.success(res?.msg || 'Lập đăng ký nguồn thu thành công!');
                await fetchBoardData();
            } else {
                toast.error(res?.msg || 'Lập đăng ký thất bại');
            }
        } catch (err: any) {
            toast.error('Lỗi khi lập đăng ký: ' + (err?.message || ''));
        } finally {
            setSaving(false);
        }
    };

    // Copy previous year revenue block
    const handleCopyPreviousBlock = async () => {
        if (!selectedClassYearId || boardData?.isPastSchoolYear) return;
        try {
            const res: any = await postRequest(
                `/cms/accountant/class-income/${selectedClassYearId}/copy-previous-revenue-block`,
                {}
            );
            if (res?.code === 200) {
                toast.success(res?.msg || 'Kế thừa khối thu thành công');
                fetchBoardData();
            } else {
                toast.error(res?.msg || 'Không thể kế thừa khối thu');
            }
        } catch {
            toast.error('Lỗi khi gọi API kế thừa khối thu');
        }
    };

    // Quick batch switch block for selected students (floating bar)
    const handleQuickBatchSwitchBlock = async () => {
        if (selectedRowKeys.length === 0) {
            toast.warn('Vui lòng tích chọn ít nhất một học sinh!');
            return;
        }
        if (!batchBlockId) {
            toast.warn('Vui lòng chọn khối thu cần chuyển!');
            return;
        }
        if (boardData?.isPastSchoolYear) {
            toast.warn('Năm học đã kết thúc, không thể thay đổi khối thu!');
            return;
        }

        const selectedStudents = boardData?.students.filter((s) => selectedRowKeys.includes(s.studentClassYearId)) || [];
        const officialStudents = selectedStudents.filter((s) =>
            (s.items || []).some((it) => !it.isPaidAnnual && it.hasInvoice && (it.invoiceStatus === 0 || it.invoiceStatus === 1))
        );

        const draftStudents = selectedStudents.filter((s) =>
            (s.items || []).some((it) => !it.isPaidAnnual && it.hasInvoice && it.invoiceStatus === -1)
        );

        const executeBatchSwitch = async () => {
            setBatchSwitchingBlock(true);
            try {
                const res: any = await patchRequest('/cms/accountant/class-income/revenue-block', {
                    studentClassYearIds: selectedRowKeys.map(Number),
                    revenueBlockId: batchBlockId,
                    month: selectedMonth,
                    year: selectedYear?.year(),
                });
                if (res?.code === 200) {
                    toast.success(res?.msg || `Đã chuyển khối thu thành công cho ${selectedRowKeys.length} học sinh`);
                    setSelectedRowKeys([]);
                    setBatchBlockId(null);
                    await fetchBoardData();
                } else {
                    toast.error(res?.msg || 'Chuyển khối thu thất bại');
                }
            } catch {
                toast.error('Lỗi khi chuyển khối thu hàng loạt');
            } finally {
                setBatchSwitchingBlock(false);
            }
        };

        if (officialStudents.length > 0 || draftStudents.length > 0) {
            const targetBlockName = revenueBlocks.find((b) => b.id === batchBlockId)?.name || 'Khối thu mới';
            Modal.confirm({
                title: `Xác nhận chuyển khối thu cho ${selectedRowKeys.length} học sinh`,
                icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                content: (
                    <div style={{ fontSize: 13, lineHeight: '20px' }}>
                        {officialStudents.length > 0 && (
                            <p style={{ marginBottom: 8 }}>
                                Có <b style={{ color: '#1677ff' }}>{officialStudents.length}</b> học sinh đã có biên lai phát hành hoặc đã thanh toán trong tháng. Các khoản thu đã phát hành/đã thu này sẽ được <b>giữ nguyên</b>.
                            </p>
                        )}
                        {draftStudents.length > 0 && (
                            <p style={{ marginBottom: 8 }}>
                                Có <b style={{ color: '#fa8c16' }}>{draftStudents.length}</b> học sinh đang có biên lai ở trạng thái <b>Chờ duyệt (nháp)</b>. Các biên lai nháp này sẽ tự động bị hủy.
                            </p>
                        )}
                        <p style={{ marginBottom: 8 }}>
                            Các khoản thu chưa tạo biên lai sẽ được đồng bộ sang khối <b>{targetBlockName}</b>. Sau khi chuyển khối, vui lòng bấm <b>[Lập đăng ký]</b> để xác nhận lại.
                        </p>
                        <p style={{ marginBottom: 0, fontWeight: 600, color: '#f5222d' }}>
                            Bạn có chắc chắn muốn tiếp tục thực hiện không?
                        </p>
                    </div>
                ),
                okText: 'Đồng ý chuyển khối',
                okType: 'primary',
                okButtonProps: { danger: true },
                cancelText: 'Hủy',
                onOk: executeBatchSwitch,
            });
            return;
        }

        await executeBatchSwitch();
    };

    // Batch assign block modal submit
    const handleBatchAssignBlockSubmit = async () => {
        if (!boardData || !targetBlockId) {
            toast.warn('Vui lòng chọn khối thu!');
            return;
        }

        let targetScyIds: number[] = [];
        if (assignTargetType === 'selected') {
            targetScyIds = selectedRowKeys.map(Number);
        } else if (assignTargetType === 'unassigned') {
            targetScyIds = boardData.students
                .filter((s) => !s.revenueBlockId)
                .map((s) => s.studentClassYearId);
        } else {
            targetScyIds = boardData.students.map((s) => s.studentClassYearId);
        }

        if (targetScyIds.length === 0) {
            toast.info('Không có học sinh nào phù hợp tiêu chí');
            setShowAssignBlockModal(false);
            return;
        }

        const targetStudents = boardData.students.filter((s) => targetScyIds.includes(s.studentClassYearId));
        const officialStudents = targetStudents.filter((s) =>
            (s.items || []).some((it) => !it.isPaidAnnual && it.hasInvoice && (it.invoiceStatus === 0 || it.invoiceStatus === 1))
        );

        const draftStudents = targetStudents.filter((s) =>
            (s.items || []).some((it) => !it.isPaidAnnual && it.hasInvoice && it.invoiceStatus === -1)
        );

        const executeBatchAssign = async () => {
            setAssigningBlock(true);
            try {
                const res: any = await patchRequest('/cms/accountant/class-income/revenue-block', {
                    studentClassYearIds: targetScyIds,
                    revenueBlockId: targetBlockId,
                    month: selectedMonth,
                    year: selectedYear?.year(),
                });
                if (res?.code === 200) {
                    toast.success(res?.msg || 'Gán khối thu thành công');
                    setShowAssignBlockModal(false);
                    setSelectedRowKeys([]);
                    await fetchBoardData();
                } else {
                    toast.error(res?.msg || 'Gán khối thu thất bại');
                }
            } catch {
                toast.error('Lỗi khi gán khối thu');
            } finally {
                setAssigningBlock(false);
            }
        };

        if (officialStudents.length > 0 || draftStudents.length > 0) {
            const targetBlockName = revenueBlocks.find((b) => b.id === targetBlockId)?.name || 'Khối thu mới';
            Modal.confirm({
                title: `Xác nhận gán khối thu cho ${targetScyIds.length} học sinh`,
                icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                content: (
                    <div style={{ fontSize: 13, lineHeight: '20px' }}>
                        {officialStudents.length > 0 && (
                            <p style={{ marginBottom: 8 }}>
                                Có <b style={{ color: '#1677ff' }}>{officialStudents.length}</b> học sinh đã có biên lai phát hành hoặc đã thanh toán trong tháng. Các khoản thu đã phát hành/đã thu này sẽ được <b>giữ nguyên</b>.
                            </p>
                        )}
                        {draftStudents.length > 0 && (
                            <p style={{ marginBottom: 8 }}>
                                Có <b style={{ color: '#fa8c16' }}>{draftStudents.length}</b> học sinh đang có biên lai ở trạng thái <b>Chờ duyệt (nháp)</b>. Các biên lai nháp này sẽ tự động bị hủy.
                            </p>
                        )}
                        <p style={{ marginBottom: 8 }}>
                            Các khoản thu chưa tạo biên lai sẽ được đồng bộ sang khối <b>{targetBlockName}</b>. Sau khi gán khối, vui lòng bấm <b>[Lập đăng ký]</b> để xác nhận lại.
                        </p>
                        <p style={{ marginBottom: 0, fontWeight: 600, color: '#f5222d' }}>
                            Bạn có chắc chắn muốn tiếp tục thực hiện không?
                        </p>
                    </div>
                ),
                okText: 'Đồng ý gán khối',
                okType: 'primary',
                okButtonProps: { danger: true },
                cancelText: 'Hủy',
                onOk: executeBatchAssign,
            });
            return;
        }

        await executeBatchAssign();
    };

    // Save cell edit on double-click (units or amount) -> Tạm tính trên FE, lưu xuống DB khi bấm [Lập đăng ký]
    const handleSaveCellEdit = (record: StudentDetailRow, field: 'units' | 'amount') => {
        if (savingCell || !focusedStudent) {
            setEditingCell(null);
            return;
        }

        const isFixed = record.collectBy !== 3;
        const originalVal = field === 'units' ? (record.expectedUnits || 1) : (record.amount || 0);
        if (cellTempValue === originalVal) {
            setEditingCell(null);
            return;
        }

        let newUnits = isFixed ? 1 : Math.max(1, Math.round(record.expectedUnits || 1));
        let newPrice = record.unitPrice || 0;
        let newAmount = record.amount || 0;

        if (field === 'units') {
            if (isFixed) {
                setEditingCell(null);
                return;
            }
            newUnits = Math.max(1, Math.round(cellTempValue || 1));
            newAmount = newUnits * newPrice;
        } else {
            newAmount = Math.max(0, cellTempValue || 0);
            newPrice = newUnits > 0 ? newAmount / newUnits : newAmount;
        }

        const scyId = record.studentClassYearId || focusedStudent.studentClassYearId;
        setLocalItemEdits((prev) => ({
            ...prev,
            [scyId]: {
                ...(prev[scyId] || {}),
                [record.incomeSchoolId]: {
                    expectedUnits: newUnits,
                    unitPrice: newPrice,
                    amount: newAmount,
                },
            },
        }));
        setEditingCell(null);
    };

    // Tạo biên lai hàng loạt (Chờ duyệt -1)
    const handleGenerateInvoices = async () => {
        if (!boardData || !selectedClassYearId || !selectedYear) return;
        setGeneratingInvoices(true);
        try {
            const payload: any = {
                classYearId: selectedClassYearId,
                month: selectedMonth,
                year: selectedYear.year(),
            };
            if (selectedRowKeys.length > 0) {
                payload.studentClassYearIds = selectedRowKeys.map(Number);
            }
            const res: any = await postRequest('/cms/accountant/class-income/generate-invoices', payload);
            if (res?.code === 200) {
                toast.success(res?.msg || 'Tạo biên lai thành công!');
                setShowGenerateInvoiceModal(false);
                setSelectedRowKeys([]);
                await fetchBoardData();
            } else {
                toast.error(res?.msg || 'Lỗi khi tạo biên lai');
            }
        } catch {
            toast.error('Lỗi khi tạo biên lai');
        } finally {
            setGeneratingInvoices(false);
        }
    };

    // Hủy biên lai nháp (-1) để mở khóa khoản thu
    const handleCancelDraftInvoice = async (incomeStudentRegisterId?: number) => {
        if (!incomeStudentRegisterId) return;
        setCancelingInvoice(true);
        try {
            const res: any = await postRequest(
                `/cms/accountant/class-income/cancel-draft-invoice?incomeStudentRegisterId=${incomeStudentRegisterId}`,
                {}
            );
            if (res?.code === 200) {
                toast.success(res?.msg || 'Đã hủy biên lai nháp và mở khóa khoản thu!');
                await fetchBoardData();
            } else {
                toast.error(res?.msg || 'Không thể hủy biên lai');
            }
        } catch {
            toast.error('Lỗi khi hủy biên lai nháp');
        } finally {
            setCancelingInvoice(false);
        }
    };

    // Export CSV of matrix
    const handleExportCSV = () => {
        if (!boardData || boardData.students.length === 0) {
            toast.warn('Không có dữ liệu để xuất');
            return;
        }

        const headers = ['STT', 'Mã HS', 'Họ và tên', 'Khối thu', 'Tự ĐKý theo tháng'];
        boardData.columns.forEach((c) => {
            headers.push(`${c.name} (${c.code || ''})`);
        });

        const rows: string[][] = [];
        boardData.students.forEach((s, idx) => {
            const row = [
                (idx + 1).toString(),
                `"${s.studentCode || ''}"`,
                `"${s.fullName || ''}"`,
                `"${s.revenueBlockName || 'Chưa gán'}"`,
                localSelfRegister[s.studentClassYearId] ? 'Có' : 'Không',
            ];
            boardData.columns.forEach((c) => {
                const isChecked = localMatrix[s.studentClassYearId]?.has(c.id);
                row.push(isChecked ? 'X' : '');
            });
            rows.push(row);
        });

        const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `Dang_ky_nguon_thu_${boardData.className}_T${selectedMonth}_${selectedYear?.year()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filtered students computed list (client-side with 300ms debounce)
    const filteredStudents = useMemo(() => {
        if (!boardData?.students) return [];

        const keyword = debouncedSearch.trim().toLowerCase();

        return boardData.students.filter((s) => {
            // 1. Filter by name or student code
            if (keyword) {
                const matchName = s.fullName ? s.fullName.toLowerCase().includes(keyword) : false;
                const matchCode = s.studentCode ? s.studentCode.toLowerCase().includes(keyword) : false;
                if (!matchName && !matchCode) return false;
            }

            // 2. Filter by revenue block
            if (filterBlockId === 'unassigned') {
                if (s.revenueBlockId) return false;
            } else if (filterBlockId !== 'all' && filterBlockId != null) {
                if (s.revenueBlockId !== filterBlockId) return false;
            }

            return true;
        });
    }, [boardData?.students, debouncedSearch, filterBlockId]);

    const isFiltering = Boolean(debouncedSearch.trim() || filterBlockId !== 'all');

    // Auto-focus first student in filtered list if current selected student is filtered out
    useEffect(() => {
        if (filteredStudents.length > 0) {
            if (!selectedStudentId || !filteredStudents.some((s) => s.studentClassYearId === selectedStudentId)) {
                setSelectedStudentId(filteredStudents[0].studentClassYearId);
            }
        }
    }, [filteredStudents, selectedStudentId]);

    // Current focused student in Zone 3
    const focusedStudent = useMemo(() => {
        if (!boardData || !selectedStudentId) return null;
        return boardData.students.find((s) => s.studentClassYearId === selectedStudentId) || null;
    }, [boardData, selectedStudentId]);

    useEffect(() => {
        setEditingCell(null);
    }, [selectedStudentId]);

    // Zone 2 Table Columns
    const matrixColumns: ColumnsType<StudentRow> = useMemo(() => {
        if (!boardData) return [];

        const fixedCols: ColumnsType<StudentRow> = [
            {
                title: 'STT',
                key: 'stt',
                width: 50,
                fixed: 'left',
                align: 'center',
                className: 'col-stt',
                render: (_, __, index) => index + 1,
            },
            {
                title: 'Mã HS',
                dataIndex: 'studentCode',
                key: 'studentCode',
                width: 120,
                fixed: 'left',
                align: 'center',
                className: 'col-code',
                render: (val) => <Text strong style={{ color: '#1677ff' }}>{val || '-'}</Text>,
            },
            {
                title: 'Họ và tên',
                dataIndex: 'fullName',
                key: 'fullName',
                width: 170,
                fixed: 'left',
                className: 'col-name',
                render: (val, record) => (
                    <div
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={() => setSelectedStudentId(record.studentClassYearId)}
                        title={val}
                    >
                        <Text strong ellipsis style={{ maxWidth: 145 }}>{val}</Text>
                        {record.studentClassYearId === selectedStudentId && (
                            <Badge color="#52c41a" style={{ marginLeft: 6, flexShrink: 0 }} />
                        )}
                    </div>
                ),
            },
            {
                title: 'Khối thu',
                dataIndex: 'revenueBlockName',
                key: 'revenueBlockName',
                width: 180,
                fixed: 'left',
                align: 'center',
                className: 'col-block',
                render: (val, record) => (
                    <Select
                        size="small"
                        style={{ width: '100%' }}
                        value={record.revenueBlockId || undefined}
                        placeholder="Chưa gán"
                        disabled={boardData.isPastSchoolYear}
                        onChange={(rbId) => {
                            setSelectedStudentId(record.studentClassYearId);
                            handleChangeSingleRevenueBlock(record.studentClassYearId, rbId);
                        }}
                    >
                        {revenueBlocks.map((rb) => (
                            <Option key={rb.id} value={rb.id}>
                                {rb.name}
                            </Option>
                        ))}
                    </Select>
                ),
            },
            {
                title: (
                    <div style={{ textAlign: 'center', padding: '0 2px' }}>
                        <Tooltip title="Tự đăng ký: Cho phép tùy chỉnh đơn giá và số lượng riêng cho học sinh">
                            <div style={{ fontWeight: 600, fontSize: 12, lineHeight: '14px', marginBottom: 2 }}>Tự ĐKý</div>
                        </Tooltip>
                        <div>
                            <Checkbox
                                checked={
                                    boardData.students.length > 0 &&
                                    boardData.students.every((s) => Boolean(localSelfRegister[s.studentClassYearId]))
                                }
                                indeterminate={
                                    boardData.students.some((s) => Boolean(localSelfRegister[s.studentClassYearId])) &&
                                    !boardData.students.every((s) => Boolean(localSelfRegister[s.studentClassYearId]))
                                }
                                disabled={boardData.isPastSchoolYear}
                                onChange={(e) => handleToggleAllSelfRegister(e.target.checked)}
                            >
                                <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>Tất cả</span>
                            </Checkbox>
                        </div>
                    </div>
                ),
                key: 'isSelfRegister',
                width: 80,
                align: 'center',
                className: 'col-self',
                render: (_, record) => {
                    const isSelf = Boolean(localSelfRegister[record.studentClassYearId]);
                    return (
                        <div
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedStudentId(record.studentClassYearId);
                            }}
                        >
                            <Checkbox
                                checked={isSelf}
                                disabled={boardData.isPastSchoolYear}
                                onChange={(e) => {
                                    setSelectedStudentId(record.studentClassYearId);
                                    handleToggleSelfRegister(record.studentClassYearId, e.target.checked);
                                }}
                            />
                        </div>
                    );
                },
            },
        ];

        // Dynamic Columns for each Income Source
        const dynamicCols: ColumnsType<StudentRow> = boardData.columns.map((col) => {
            const allChecked = boardData.students.length > 0 && boardData.students.every((s) => {
                return localMatrix[s.studentClassYearId]?.has(col.id);
            });
            const someChecked = boardData.students.length > 0 && boardData.students.some((s) => {
                return localMatrix[s.studentClassYearId]?.has(col.id);
            });
            const isIndeterminate = someChecked && !allChecked;
            const collectInfo = COLLECT_BY_LABEL[col.collectBy] || { text: 'Tháng', color: 'default' };

            return {
                title: (
                    <div style={{ textAlign: 'center', padding: '0 4px' }}>
                        <Tooltip title={col.name} placement="top">
                            <div
                                style={{
                                    fontWeight: 600,
                                    fontSize: 12,
                                    lineHeight: '15px',
                                    marginBottom: 3,
                                    maxHeight: 30,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    cursor: 'help',
                                }}
                            >
                                {col.name}
                            </div>
                        </Tooltip>
                        <Space size={2} style={{ marginBottom: 3 }}>
                            <Tag color={collectInfo.color} style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '15px', height: 16 }}>
                                {collectInfo.text}
                            </Tag>
                            {col.isEinvoice && <Tag color="gold" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '15px', height: 16 }}>HĐĐT</Tag>}
                        </Space>
                        <div>
                            <Checkbox
                                checked={allChecked}
                                indeterminate={isIndeterminate}
                                disabled={boardData.isPastSchoolYear}
                                onChange={(e) => handleToggleColumnAll(col.id, e.target.checked)}
                            >
                                <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>Tất cả</span>
                            </Checkbox>
                        </div>
                    </div>
                ),
                key: `income_${col.id}`,
                width: 125,
                align: 'center',
                className: 'col-income',
                onCell: (record) => {
                    const isChecked = localMatrix[record.studentClassYearId]?.has(col.id) || false;
                    const item = (record.items || []).find((it) => it.incomeSchoolId === col.id);
                    const hasDebt = !isChecked && Boolean(item && item.carryAmount != null && item.carryAmount < 0);
                    if (hasDebt) {
                        return {
                            style: {
                                backgroundColor: '#fff7e6',
                                boxShadow: 'inset 0 0 0 1px #ffd591',
                                transition: 'all 0.2s',
                            },
                        };
                    }
                    return {};
                },
                render: (_, record) => {
                    const isChecked = localMatrix[record.studentClassYearId]?.has(col.id) || false;
                    const isLocked = record.lockedIncomeIds?.includes(col.id) || false;
                    const item = (record.items || []).find((it) => it.incomeSchoolId === col.id);
                    const hasDebt = !isChecked && Boolean(item && item.carryAmount != null && item.carryAmount < 0);
                    const isPaidAnnual = Boolean(item && item.isPaidAnnual);

                    const checkboxNode = (
                        <div
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedStudentId(record.studentClassYearId);
                            }}
                        >
                            {isPaidAnnual ? (
                                <Tooltip title={item?.paidAnnualNote || "Khoản thu này đã thanh toán trong năm học. Không lập biên lai mới."}>
                                    <Tag color="success" style={{ cursor: 'default', margin: 0, padding: '1px 6px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                                        <CheckCircleOutlined />
                                        <span>Đã nộp</span>
                                        <LockOutlined style={{ fontSize: 10 }} />
                                    </Tag>
                                </Tooltip>
                            ) : isLocked ? (
                                <Tooltip title="Khoản thu này đã xuất biên lai. Khóa không thể bỏ chọn.">
                                    <Checkbox checked={true} disabled={true}>
                                        <LockOutlined style={{ color: '#faad14', marginLeft: 2 }} />
                                    </Checkbox>
                                </Tooltip>
                            ) : (
                                <Checkbox
                                    checked={isChecked}
                                    disabled={boardData.isPastSchoolYear}
                                    onChange={() => {
                                        setSelectedStudentId(record.studentClassYearId);
                                        handleToggleCell(record.studentClassYearId, col.id, false);
                                    }}
                                />
                            )}
                        </div>
                    );

                    if (hasDebt && item?.carryAmount) {
                        const debtMsg = item.hasInvoice
                            ? `Nợ tháng trước: ${Math.abs(item.carryAmount).toLocaleString('vi-VN')} đ (Đã xuất biên lai nợ). Nhấp để chọn đăng ký sử dụng thêm tháng này.`
                            : `Nợ tháng trước: ${Math.abs(item.carryAmount).toLocaleString('vi-VN')} đ (Chưa đăng ký tháng này). Nhấp để chọn đăng ký thêm.`;
                        return (
                            <Tooltip
                                title={debtMsg}
                                placement="top"
                            >
                                {checkboxNode}
                            </Tooltip>
                        );
                    }

                    return checkboxNode;
                },
            };
        });

        return [...fixedCols, ...dynamicCols];
    }, [boardData, localMatrix, localSelfRegister, selectedStudentId, revenueBlocks]);

    // Matrix scroll width calculation
    const matrixScrollX = useMemo(() => {
        if (!boardData) return 1000;
        // Selection(40) + STT(50) + Mã HS(120) + Họ và tên(170) + Khối thu(180) = 560px (fixed)
        // Tự ĐKý(80px)
        const fixedWidth = 644;
        const dynamicWidth = (boardData.columns || []).length * 125;
        return fixedWidth + dynamicWidth;
    }, [boardData]);

    // Zone 3 Detail Columns for Focused Student (8 streamlined columns + Action column)
    const zone3Columns: ColumnsType<StudentDetailRow> = [
        {
            title: 'Mã',
            dataIndex: 'code',
            key: 'code',
            width: 55,
            align: 'center',
            fixed: 'left',
            className: 'col-detail-code',
            render: (val) => <Text strong style={{ color: '#595959' }}>{val || ''}</Text>,
        },
        {
            title: 'Nội dung',
            dataIndex: 'name',
            key: 'name',
            width: 240,
            align: 'left',
            fixed: 'left',
            className: 'col-detail-name',
            render: (val, r) => {
                const collectInfo = COLLECT_BY_DETAIL_LABEL[r.collectBy] || (r.collectBy ? { text: `Loại ${r.collectBy}`, color: 'default' } : null);

                return (
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                            <Text
                                strong={r.isRegistered}
                                style={{
                                    color: r.isRegistered ? '#262626' : (r.carryAmount != null && r.carryAmount < 0 ? '#d46b08' : '#8c8c8c'),
                                    flex: 1,
                                }}
                                title={val}
                                ellipsis
                            >
                                {val}
                            </Text>
                            {collectInfo && (
                                <Tag color={collectInfo.color} style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px', height: 16, flexShrink: 0 }}>
                                    {collectInfo.text}
                                </Tag>
                            )}
                        </div>
                        {r.hasInvoice && (
                            <Space size={4} wrap>
                                {r.invoiceStatus === -1 && <Tag color="warning" style={{ fontSize: 11, margin: 0 }}>Chờ duyệt</Tag>}
                                {r.invoiceStatus === 0 && <Tag color="processing" style={{ fontSize: 11, margin: 0 }}>Chưa TT</Tag>}
                                {r.invoiceStatus === 1 && <Tag color="success" style={{ fontSize: 11, margin: 0 }}>Đã TT</Tag>}
                                {r.invoiceId && <Text type="secondary" style={{ fontSize: 11 }}>#{r.invoiceId}</Text>}
                            </Space>
                        )}
                        {!r.isRegistered && r.carryAmount != null && r.carryAmount < 0 && !r.hasInvoice && (
                            <Tag color="orange" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px', height: 16 }}>Nợ khối cũ</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: (
                <Tooltip title="Số lượng thừa/nợ tháng trước chuyển sang. Dương (+): Thừa; Âm (-): Nợ">
                    <span>SL thừa</span>
                </Tooltip>
            ),
            dataIndex: 'carryUnits',
            key: 'carryUnits',
            width: 75,
            align: 'right',
            className: 'col-detail-units',
            render: (val) => {
                if (val == null || val === 0) return '';
                if (val > 0) return <Text strong style={{ color: '#389e0d' }}>+{val}</Text>;
                return <Text strong style={{ color: '#cf1322' }}>{val}</Text>;
            },
        },
        {
            title: (
                <Tooltip title="Số tiền thừa/nợ tháng trước chuyển sang. Dương (+): Thừa; Âm (-): Nợ">
                    <span>Tiền thừa</span>
                </Tooltip>
            ),
            dataIndex: 'carryAmount',
            key: 'carryAmount',
            width: 105,
            align: 'right',
            className: 'col-detail-amount',
            render: (val) => {
                if (val == null || val === 0) return '';
                if (val > 0) return <Text strong style={{ color: '#389e0d' }}>+{Number(val).toLocaleString('vi-VN')}</Text>;
                return <Text strong style={{ color: '#cf1322' }}>{Number(val).toLocaleString('vi-VN')}</Text>;
            },
        },
        {
            title: (
                <Tooltip title="Số lượng đăng ký của tháng hiện tại">
                    <span>SL ĐKý</span>
                </Tooltip>
            ),
            dataIndex: 'expectedUnits',
            key: 'expectedUnits',
            width: 75,
            align: 'right',
            className: 'col-detail-units',
            render: (val, r) => {
                if (!r.isRegistered || !val || val <= 0) return '';

                const isEditingThis = editingCell?.incomeSchoolId === r.incomeSchoolId && editingCell?.field === 'units';
                if (isEditingThis) {
                    return (
                        <InputNumber
                            min={1}
                            precision={0}
                            size="small"
                            autoFocus
                            value={cellTempValue}
                            onChange={(v) => setCellTempValue(v ?? 1)}
                            onPressEnter={() => handleSaveCellEdit(r, 'units')}
                            onBlur={() => handleSaveCellEdit(r, 'units')}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setEditingCell(null);
                            }}
                            disabled={savingCell}
                            style={{ width: '100%' }}
                        />
                    );
                }

                const isSelf = Boolean(focusedStudent ? localSelfRegister[focusedStudent.studentClassYearId] : false);
                const isFixed = r.collectBy !== 3;
                const canEdit = isSelf && !isFixed && !boardData?.isPastSchoolYear && !r.isLocked;

                return (
                    <div
                        style={{
                            cursor: canEdit ? 'pointer' : 'default',
                            padding: '2px 4px',
                            borderRadius: 4,
                            minHeight: 22,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                        }}
                        className={canEdit ? 'editable-cell-hover' : ''}
                        title={
                            canEdit
                                ? 'Nhấp đúp chuột để sửa số lượng'
                                : isFixed
                                    ? 'Khoản thu theo tháng/kỳ/năm có số lượng cố định là 1'
                                    : undefined
                        }
                        onDoubleClick={() => {
                            if (!canEdit) {
                                if (!isSelf) {
                                    toast.info('Vui lòng tích chọn "Tự ĐKý" cho học sinh để chỉnh sửa số lượng / tiền đăng ký');
                                } else if (isFixed) {
                                    toast.info('Khoản thu cố định theo tháng/kỳ/năm có số lượng mặc định là 1. Bạn chỉ có thể chỉnh sửa cột Tiền ĐKý.');
                                }
                                return;
                            }
                            setEditingCell({ incomeSchoolId: r.incomeSchoolId, field: 'units' });
                            setCellTempValue(r.expectedUnits || 1);
                        }}
                    >
                        <Text strong>{val}</Text>
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="Tiền đăng ký của tháng hiện tại">
                    <span>Tiền ĐKý</span>
                </Tooltip>
            ),
            dataIndex: 'amount',
            key: 'amount',
            width: 105,
            align: 'right',
            className: 'col-detail-amount',
            render: (val, r) => {
                if (!r.isRegistered) return '';

                const isEditingThis = editingCell?.incomeSchoolId === r.incomeSchoolId && editingCell?.field === 'amount';
                if (isEditingThis) {
                    return (
                        <InputNumber
                            min={0}
                            step={1000}
                            size="small"
                            autoFocus
                            value={cellTempValue}
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(v) => (v ? v.replace(/\$\s?|(,*)/g, '') : '') as any}
                            onChange={(v) => setCellTempValue(Number(v) || 0)}
                            onPressEnter={() => handleSaveCellEdit(r, 'amount')}
                            onBlur={() => handleSaveCellEdit(r, 'amount')}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setEditingCell(null);
                            }}
                            disabled={savingCell}
                            style={{ width: '100%' }}
                        />
                    );
                }

                const isSelf = Boolean(focusedStudent ? localSelfRegister[focusedStudent.studentClassYearId] : false);
                const canEdit = isSelf && !boardData?.isPastSchoolYear && !r.isLocked;

                return (
                    <div
                        style={{
                            cursor: canEdit ? 'pointer' : 'default',
                            padding: '2px 4px',
                            borderRadius: 4,
                            minHeight: 22,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                        }}
                        className={canEdit ? 'editable-cell-hover' : ''}
                        title={canEdit ? 'Nhấp đúp chuột để sửa tiền đăng ký' : undefined}
                        onDoubleClick={() => {
                            if (!canEdit) {
                                if (!isSelf) {
                                    toast.info('Vui lòng tích chọn "Tự ĐKý" cho học sinh để chỉnh sửa số lượng / tiền đăng ký');
                                }
                                return;
                            }
                            setEditingCell({ incomeSchoolId: r.incomeSchoolId, field: 'amount' });
                            setCellTempValue(r.amount || 0);
                        }}
                    >
                        <Text strong style={{ color: r.isPaidAnnual ? '#8c8c8c' : '#1677ff' }}>
                            {Number(val || 0).toLocaleString('vi-VN')}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="Số lượng thực tế phải thu sau khi khấu trừ thừa / cộng nợ">
                    <span>SL Pthu</span>
                </Tooltip>
            ),
            dataIndex: 'mustCollectUnits',
            key: 'mustCollectUnits',
            width: 75,
            align: 'right',
            className: 'col-detail-units',
            render: (val, r) => {
                if (!r.isRegistered) return '';
                if (r.isPaidAnnual) return <Text type="secondary">0</Text>;
                return <Text strong>{val ?? 0}</Text>;
            },
        },
        {
            title: (
                <Tooltip title="Số tiền thực tế phải thu sau khi khấu trừ thừa / tách nợ">
                    <span>Tiền thu</span>
                </Tooltip>
            ),
            dataIndex: 'mustCollectAmount',
            key: 'mustCollectAmount',
            width: 110,
            align: 'right',
            className: 'col-detail-amount',
            render: (val, r) => {
                if (!r.isRegistered && (!r.carryAmount || r.carryAmount >= 0)) return '';
                if (r.isPaidAnnual) {
                    return <Text type="secondary" style={{ fontSize: 13 }}>0 đ</Text>;
                }
                return (
                    <Text strong style={{ color: '#cf1322', fontSize: 13 }}>
                        {Number(val || 0).toLocaleString('vi-VN')}
                    </Text>
                );
            },
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 105,
            align: 'center',
            render: (_, r) => {
                if (!r.isRegistered && (!r.carryAmount || r.carryAmount >= 0) && !r.hasInvoice) return '';
                if (r.hasInvoice && r.invoiceStatus === -1) {
                    return (
                        <Popconfirm
                            title="Hủy biên lai nháp?"
                            description="Hệ thống sẽ xóa biên lai nháp và mở khóa khoản thu này để bạn chỉnh sửa."
                            onConfirm={() => handleCancelDraftInvoice(r.id)}
                            okText="Hủy biên lai"
                            cancelText="Đóng"
                            okButtonProps={{ danger: true, loading: cancelingInvoice }}
                        >
                            <Button size="small" danger>
                                Hủy nháp
                            </Button>
                        </Popconfirm>
                    );
                }
                if (r.isPaidAnnual) {
                    return (
                        <Tooltip title={r.paidAnnualNote || "Khoản thu này đã hoàn thành trong năm học"}>
                            <Text strong style={{ color: '#52c41a', fontSize: 12 }}>
                                Đã thu năm
                            </Text>
                        </Tooltip>
                    );
                }
                if (r.isRegistered && !r.isLocked) {
                    return (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Chưa tạo biên lai
                        </Text>
                    );
                }
                if (r.hasInvoice) {
                    return (
                        <Text style={{ color: r.invoiceStatus === 1 ? '#52c41a' : '#1677ff', fontSize: 12 }}>
                            {r.invoiceStatus === 1 ? 'Đã thu' : 'Đã khóa'}
                        </Text>
                    );
                }
                if (!r.isRegistered && r.carryAmount != null && r.carryAmount < 0) {
                    return (
                        <Text style={{ color: '#fa8c16', fontSize: 12 }}>
                            Chưa tạo bill nợ
                        </Text>
                    );
                }
                return (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Chưa tạo biên lai
                    </Text>
                );
            },
        },
    ];

    // Detail Table Data: Streamlined for Zone 3
    const detailTableData = useMemo<StudentDetailRow[]>(() => {
        if (!focusedStudent || !boardData) return [];
        const checkedSet = localMatrix[focusedStudent.studentClassYearId] || new Set();

        const allRows: StudentDetailRow[] = boardData.columns.map((col): StudentDetailRow => {
            const isRegistered = checkedSet.has(col.id);
            const item = (focusedStudent.items || []).find((it) => it.incomeSchoolId === col.id);
            const isLocked = Boolean(focusedStudent.lockedIncomeIds?.includes(col.id) || item?.isPaidAnnual);

            // Tìm blockDefault: ưu tiên khối hiện tại của học sinh
            let blockDefault = focusedStudent.revenueBlockId && boardData.blockDefaults
                ? boardData.blockDefaults[focusedStudent.revenueBlockId]?.[col.id]
                : undefined;
            // Fallback: nếu khối hiện tại không có nguồn này, tìm trong bất kỳ khối nào có nguồn này
            if (!blockDefault && boardData.blockDefaults) {
                for (const bId of Object.keys(boardData.blockDefaults)) {
                    if (boardData.blockDefaults[Number(bId)]?.[col.id]) {
                        blockDefault = boardData.blockDefaults[Number(bId)][col.id];
                        break;
                    }
                }
            }

            if (isRegistered) {
                const isSelf = Boolean(localSelfRegister[focusedStudent.studentClassYearId]);
                const origSelf = Boolean(focusedStudent.isSelfRegister ?? (focusedStudent as any).selfRegister);
                const localEdit = isSelf ? localItemEdits[focusedStudent.studentClassYearId]?.[col.id] : undefined;
                const isFixed = col.collectBy !== 3;
                const defUnits = isFixed ? 1 : (blockDefault?.units && blockDefault.units > 0 ? blockDefault.units : 1);
                const defPrice = blockDefault?.unitPrice != null ? blockDefault.unitPrice : 0;

                const hasDbRecord = Boolean(item && item.id && item.id > 0 && item.expectedUnits != null && item.expectedUnits > 0);
                const justUncheckedSelf = origSelf && !isSelf && blockDefault != null && !isLocked;

                // Nếu đã có nguồn thu trong DB thì ưu tiên lấy theo DB; chưa có trong DB hoặc vừa bỏ tích Tự ĐKý thì lấy từ Đăng ký nhanh
                const baseUnits = hasDbRecord
                    ? (isFixed ? 1 : item!.expectedUnits)
                    : defUnits;
                const basePrice = (hasDbRecord && item!.unitPrice != null)
                    ? item!.unitPrice
                    : defPrice;

                const expectedUnits = localEdit ? localEdit.expectedUnits : (justUncheckedSelf ? defUnits : baseUnits);
                const unitPrice = localEdit ? localEdit.unitPrice : (justUncheckedSelf ? defPrice : basePrice);
                const amount = localEdit ? localEdit.amount : (expectedUnits * unitPrice);

                const carryUnits = item?.carryUnits != null ? item.carryUnits : 0;
                const carryAmount = item?.carryAmount != null ? item.carryAmount : 0;
                const isPaidAnnual = Boolean(item?.isPaidAnnual);
                const mustCollectUnits = isPaidAnnual ? 0 : Math.max(0, expectedUnits - carryUnits);
                const mustCollectAmount = isPaidAnnual ? 0 : Math.max(0, amount - carryAmount);
                const hasInvoice = item?.hasInvoice || false;
                const invoiceStatus = item?.invoiceStatus;
                const invoiceId = item?.invoiceId;

                return {
                    studentClassYearId: focusedStudent.studentClassYearId,
                    incomeSchoolId: col.id,
                    code: col.code || '',
                    name: col.name,
                    collectBy: col.collectBy,
                    isRegistered: true,
                    isLocked,
                    id: item?.id,
                    carryUnits,
                    carryAmount,
                    expectedUnits,
                    unitPrice,
                    amount,
                    mustCollectUnits,
                    mustCollectAmount,
                    hasInvoice,
                    invoiceStatus,
                    invoiceId,
                    isPaidAnnual: Boolean(item?.isPaidAnnual),
                    paidAnnualNote: item?.paidAnnualNote,
                };
            }

            // Case A: Không đăng ký tháng này (isRegistered = false)
            // Giữ lại carryUnits, carryAmount, mustCollectAmount từ item nếu có số liệu nợ/thừa/hóa đơn
            const carryUnits = item?.carryUnits != null ? item.carryUnits : null;
            const carryAmount = item?.carryAmount != null ? item.carryAmount : null;
            const hasDebt = carryAmount != null && carryAmount < 0;
            const debtAmount = hasDebt ? Math.abs(carryAmount) : 0;

            const isPaidAnnual = Boolean(item?.isPaidAnnual);

            return {
                studentClassYearId: focusedStudent.studentClassYearId,
                incomeSchoolId: col.id,
                code: col.code || '',
                name: col.name,
                collectBy: col.collectBy,
                isRegistered: false,
                isLocked: false,
                id: item?.id,
                carryUnits,
                carryAmount,
                expectedUnits: null,
                unitPrice: null,
                amount: null,
                mustCollectUnits: null,
                mustCollectAmount: isPaidAnnual ? 0 : (hasDebt ? debtAmount : null),
                hasInvoice: item?.hasInvoice || false,
                invoiceStatus: item?.invoiceStatus ?? null,
                invoiceId: item?.invoiceId ?? null,
                isPaidAnnual,
                paidAnnualNote: item?.paidAnnualNote,
            };
        });

        if (showAllIncomes) {
            return allRows;
        }
        // Khi bỏ tích "Hiện tất cả nguồn thu", ẩn luôn các nguồn đã thanh toán hoàn tất (theo năm hoặc đã TT toàn bộ trong tháng)
        return allRows.filter((r: StudentDetailRow) => {
            const isPaid = Boolean(
                r.isPaidAnnual ||
                (r.hasInvoice && r.invoiceStatus === 1 && (!r.isRegistered || r.isLocked))
            );
            if (isPaid) return false;

            return r.isRegistered || (r.carryAmount != null && r.carryAmount !== 0) || Boolean(r.hasInvoice);
        });
    }, [focusedStudent, boardData, localMatrix, localSelfRegister, localItemEdits, showAllIncomes]);

    // Kiểm tra xem có thay đổi chưa bấm [Lập đăng ký] trên giao diện không
    const hasUnsavedChanges = useMemo(() => {
        if (!boardData) return false;
        if (Object.keys(localItemEdits).some((scyId) => Object.keys(localItemEdits[Number(scyId)] || {}).length > 0)) {
            return true;
        }
        for (const s of boardData.students) {
            const scyId = s.studentClassYearId;
            const origSelf = Boolean(s.isSelfRegister ?? (s as any).selfRegister);
            const curSelf = Boolean(localSelfRegister[scyId]);
            if (origSelf !== curSelf) return true;

            const origSet = new Set(s.registeredIncomeIds || []);
            const curSet = localMatrix[scyId] || new Set();
            if (origSet.size !== curSet.size) return true;
            for (const id of Array.from(curSet)) {
                if (!origSet.has(id)) return true;
            }
        }
        return false;
    }, [boardData, localMatrix, localSelfRegister, localItemEdits]);

    // Kiểm tra xem lớp hoặc học sinh đang chọn có khoản thu đang tích nhưng chưa lưu đăng ký vào DB (preview id = 0), mới đổi khối thu, hoặc lệch đơn giá gói
    const hasUnregisteredData = useMemo(() => {
        if (!boardData) return true;
        if (!boardData.lastRegisteredDate) return true;
        const targetStudents = selectedRowKeys.length > 0
            ? boardData.students.filter((s) => selectedRowKeys.includes(s.studentClassYearId))
            : boardData.students;
        for (const s of targetStudents) {
            const checkedSet = localMatrix[s.studentClassYearId] || new Set();
            const isSelf = Boolean(localSelfRegister[s.studentClassYearId]);
            for (const incId of Array.from(checkedSet)) {
                const item = (s.items || []).find((it) => it.incomeSchoolId === incId);
                if (item?.isPaidAnnual) continue;
                const isItemLocked = Boolean(s.lockedIncomeIds?.includes(incId));
                // Học sinh mới gán/đổi khối thu (chưa có registeredDate) hoặc khoản thu chưa lưu vào DB (id = 0) hoặc mới tích từ ô nợ (expectedUnits <= 0)
                if (!s.registeredDate || !item || !item.id || item.id === 0 || item.expectedUnits <= 0) {
                    return true;
                }
                // Nếu học sinh không tích Tự ĐKý và chưa có biên lai tháng này, kiểm tra xem đơn giá/số lượng trong DB có lệch với gói Đăng ký nhanh hiện tại không
                if (!isSelf && !isItemLocked && s.revenueBlockId && boardData.blockDefaults) {
                    const blockDef = boardData.blockDefaults[s.revenueBlockId]?.[incId];
                    if (blockDef) {
                        const col = boardData.columns.find((c) => c.id === incId);
                        const isFixed = col ? col.collectBy !== 3 : true;
                        const defUnits = isFixed ? 1 : (blockDef.units > 0 ? blockDef.units : 1);
                        const defPrice = blockDef.unitPrice ?? 0;
                        if (item.expectedUnits !== defUnits || item.unitPrice !== defPrice) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }, [boardData, localMatrix, localSelfRegister, selectedRowKeys]);

    const disableGenerateInvoice = Boolean(
        boardData?.isPastSchoolYear || !boardData || hasUnsavedChanges || hasUnregisteredData
    );
    const generateInvoiceTooltip = hasUnsavedChanges
        ? 'Thông tin đăng ký vừa thay đổi. Vui lòng bấm [Lập đăng ký] để lưu cập nhật trước khi tạo biên lai!'
        : hasUnregisteredData
            ? 'Còn khoản thu chưa được lập đăng ký. Vui lòng bấm [Lập đăng ký] trước khi tạo biên lai!'
            : 'Tạo biên lai chờ duyệt cho các khoản thu đã đăng ký';

    const registeredCount = useMemo(() => {
        if (!focusedStudent || !boardData) return 0;
        const checkedSet = localMatrix[focusedStudent.studentClassYearId];
        return checkedSet ? checkedSet.size : 0;
    }, [focusedStudent, boardData, localMatrix]);

    const focusedStudentTotalMustCollectAmount = useMemo(() => {
        return detailTableData.reduce((acc, it) => acc + (it.mustCollectAmount || 0), 0);
    }, [detailTableData]);

    return (
        <div className="class-income-board-page" style={{ padding: '0 8px 24px 8px' }}>
            {/* VÙNG 1: TOOLBAR & BỘ LỌC */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    marginBottom: 16,
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: '16px 20px' }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 16,
                    }}
                >
                    {/* Left: Filters with aligned inline labels */}
                    <Space wrap size={16} align="center">
                        <Tag
                            color="blue"
                            style={{
                                fontSize: 13,
                                padding: '4px 12px',
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                            }}
                        >
                            <CalendarOutlined /> Kỳ làm việc: Tháng {selectedMonth}/{selectedYear ? selectedYear.year() : ''}
                        </Tag>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Khối:</Text>
                            <ClassLevelSelect
                                value={selectedClassLevel}
                                onChange={handleClassLevelChange}
                                placeholder="Tất cả khối"
                                style={{ width: 130 }}
                            />
                        </Space>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Lớp:</Text>
                            <Select
                                value={selectedClassYearId || undefined}
                                onChange={handleClassChange}
                                placeholder="Chọn lớp"
                                style={{ width: 160 }}
                                showSearch
                                optionFilterProp="children"
                            >
                                {filteredClassList.map((c) => (
                                    <Option key={c.classYearId} value={c.classYearId}>
                                        {c.className}
                                    </Option>
                                ))}
                            </Select>
                        </Space>

                        <Tooltip title="Tải lại dữ liệu">
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchBoardData}
                                loading={loading}
                            />
                        </Tooltip>

                        {/* Thẻ trạng thái lần lập đăng ký gần nhất trong tháng */}
                        {boardData && (
                            boardData.lastRegisteredDate ? (
                                <Tooltip title="Lớp đã được lập đăng ký nguồn thu trong tháng này. Bạn vẫn có thể tích chọn lại và bấm [Lập đăng ký] để cập nhật bất cứ lúc nào.">
                                    <Tag
                                        color="success"
                                        icon={<CheckCircleOutlined />}
                                        style={{
                                            fontSize: 13,
                                            padding: '4px 12px',
                                            borderRadius: 4,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Lần lập gần nhất: {dayjs(boardData.lastRegisteredDate).format('HH:mm DD/MM/YYYY')}
                                    </Tag>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Lớp chưa từng được bấm lưu đăng ký trong tháng. Danh sách đang hiển thị theo cấu hình mặc định từ Khối thu.">
                                    <Tag
                                        icon={<ClockCircleOutlined />}
                                        style={{
                                            fontSize: 13,
                                            padding: '4px 12px',
                                            borderRadius: 4,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            color: '#8c8c8c',
                                            background: '#fafafa',
                                            borderColor: '#d9d9d9',
                                        }}
                                    >
                                        Chưa lập đăng ký tháng này
                                    </Tag>
                                </Tooltip>
                            )
                        )}
                    </Space>

                    {/* Right: Grouped Actions */}
                    <Space wrap size={10} align="center">
                        {/* Nhóm thao tác Khối thu */}
                        <Space.Compact>
                            <Button
                                icon={<TeamOutlined />}
                                disabled={boardData?.isPastSchoolYear || !boardData}
                                onClick={() => {
                                    if (selectedRowKeys.length > 0) {
                                        setAssignTargetType('selected');
                                    } else {
                                        setAssignTargetType('all');
                                    }
                                    setShowAssignBlockModal(true);
                                }}
                            >
                                Gán khối thu
                            </Button>
                            <Popconfirm
                                title="Kế thừa khối thu từ năm trước?"
                                description="Hệ thống sẽ tra cứu khối thu của các học sinh này ở năm học liền trước để tự động gán."
                                onConfirm={handleCopyPreviousBlock}
                                okText="Xác nhận"
                                cancelText="Hủy"
                                disabled={boardData?.isPastSchoolYear || !boardData}
                            >
                                <Tooltip title="Kế thừa khối thu từ năm học liền trước">
                                    <Button
                                        icon={<HistoryOutlined />}
                                        disabled={boardData?.isPastSchoolYear || !boardData}
                                    >
                                        Lấy khối năm trước
                                    </Button>
                                </Tooltip>
                            </Popconfirm>
                        </Space.Compact>

                        {/* Tiện ích xuất */}
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportCSV}
                            disabled={!boardData || boardData.students.length === 0}
                        >
                            Xuất Excel
                        </Button>

                        {/* Chuyển màn hình */}
                        {selectedClassYearId && (
                            <Tooltip title="Chuyển sang màn in biên lai chi tiết cho lớp (Phương án P1)">
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={() => navigate(`/detail-class-year-income/${selectedClassYearId}`)}
                                    style={{ borderColor: '#722ed1', color: '#722ed1' }}
                                >
                                    In biên lai (P1)
                                </Button>
                            </Tooltip>
                        )}

                        {/* Tác vụ chính nổi bật nhất */}
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            disabled={boardData?.isPastSchoolYear || !boardData}
                            onClick={handleBatchRegister}
                            style={{
                                backgroundColor: '#1677ff',
                                fontWeight: 600,
                                borderRadius: 6,
                                paddingLeft: 16,
                                paddingRight: 16,
                                boxShadow: '0 2px 6px rgba(22, 119, 255, 0.3)',
                            }}
                        >
                            Lập đăng ký
                        </Button>

                        {/* Nút Tạo biên lai riêng biệt từng nguồn thu */}
                        <Tooltip title={generateInvoiceTooltip}>
                            <span style={{ display: 'inline-block' }}>
                                <Button
                                    type="primary"
                                    icon={<FileTextOutlined />}
                                    loading={generatingInvoices}
                                    disabled={disableGenerateInvoice}
                                    onClick={() => setShowGenerateInvoiceModal(true)}
                                    style={{
                                        backgroundColor: disableGenerateInvoice ? undefined : '#52c41a',
                                        borderColor: disableGenerateInvoice ? undefined : '#52c41a',
                                        fontWeight: 600,
                                        borderRadius: 6,
                                        paddingLeft: 16,
                                        paddingRight: 16,
                                        boxShadow: disableGenerateInvoice ? undefined : '0 2px 6px rgba(82, 196, 26, 0.3)',
                                    }}
                                >
                                    Tạo biên lai {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length} HS)` : '(Cả lớp)'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Space>
                </div>

                {/* Banners */}
                {boardData?.isPastSchoolYear && (
                    <Alert
                        message={boardData.isMonthLocked ? 'Tháng này đã kết chuyển sang tháng sau và khóa sổ' : 'Năm học đã kết thúc'}
                        description={
                            boardData.isMonthLocked
                                ? 'Dữ liệu nguồn thu của tháng này đã được kết chuyển số dư sang tháng kế tiếp và khóa sổ. Toàn bộ thông tin chỉ ở chế độ xem (Read-only).'
                                : 'Lớp học này thuộc năm học đã qua hoặc đã đóng. Toàn bộ thông tin đăng ký chỉ ở chế độ xem (Read-only).'
                        }
                        type="info"
                        showIcon
                        style={{ marginTop: 12, borderRadius: 8 }}
                    />
                )}

                {boardData && boardData.unassignedBlockCount > 0 && !boardData.isPastSchoolYear && (
                    <Alert
                        message={
                            <span>
                                Có <Text strong style={{ color: '#d46b08' }}>{boardData.unassignedBlockCount}</Text> học sinh chưa được gán Khối thu.
                                Hãy bấm <Button size="small" type="link" onClick={() => setShowAssignBlockModal(true)} style={{ padding: '0 4px' }}>[Gán khối thu]</Button> để gán nhanh cho cả lớp hoặc chọn trực tiếp trên từng dòng.
                            </span>
                        }
                        type="warning"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={{ marginTop: 12, borderRadius: 8 }}
                    />
                )}
            </Card>

            {/* VÙNG 2: LƯỚI MA TRẬN ĐĂNG KÝ NGUỒN THU CỦA LỚP */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    marginBottom: 16,
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: '12px 16px' }}
                title={
                    <Space size={8}>
                        <Title level={5} style={{ margin: 0 }}>
                            Danh sách học sinh & Ma trận nguồn thu lớp {boardData?.className || ''}
                        </Title>
                        {boardData && (
                            <Tag color="cyan">
                                Sĩ số: {boardData.totalStudents} học sinh
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    <Space size={10} wrap align="center">
                        <Input
                            placeholder="Tìm tên hoặc mã HS..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            allowClear
                            size="small"
                            style={{ width: 190, borderRadius: 6 }}
                        />

                        <Select
                            size="small"
                            value={filterBlockId}
                            onChange={(val) => setFilterBlockId(val)}
                            style={{ width: 170 }}
                            placeholder="Lọc khối thu"
                        >
                            <Option value="all">Tất cả khối thu</Option>
                            <Option value="unassigned">Chưa gán khối ({boardData?.unassignedBlockCount || 0})</Option>
                            {revenueBlocks.map((rb) => (
                                <Option key={rb.id} value={rb.id}>
                                    {rb.name} ({rb.code})
                                </Option>
                            ))}
                        </Select>

                        {isFiltering && (
                            <Tag color="orange" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
                                Hiển thị: {filteredStudents.length}/{boardData?.totalStudents || 0}
                            </Tag>
                        )}
                    </Space>
                }
            >
                {selectedRowKeys.length > 0 && (
                    <div
                        style={{
                            marginBottom: 12,
                            padding: '8px 16px',
                            background: 'linear-gradient(90deg, #e6f4ff 0%, #f0f5ff 100%)',
                            border: '1px solid #91caff',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 12,
                            boxShadow: '0 2px 6px rgba(22, 119, 255, 0.08)',
                        }}
                    >
                        <Space size={12} align="center">
                            <Text strong style={{ color: '#0958d9', fontSize: 13 }}>
                                Đã chọn: <Badge count={selectedRowKeys.length} overflowCount={999} style={{ backgroundColor: '#1677ff' }} /> học sinh
                            </Text>
                            <Button size="small" onClick={() => setSelectedRowKeys([])}>
                                Bỏ chọn tất cả
                            </Button>
                        </Space>
                        <Space size={10} align="center">
                            <Text strong style={{ fontSize: 13, color: '#262626' }}>Chuyển nhanh sang khối:</Text>
                            <Select
                                size="small"
                                placeholder="Chọn khối thu đích"
                                style={{ width: 200 }}
                                value={batchBlockId || undefined}
                                onChange={(val) => setBatchBlockId(val)}
                                disabled={boardData?.isPastSchoolYear}
                            >
                                {revenueBlocks.map((rb) => (
                                    <Option key={rb.id} value={rb.id}>
                                        {rb.name} ({rb.code})
                                    </Option>
                                ))}
                            </Select>
                            <Button
                                size="small"
                                type="primary"
                                icon={<SwapOutlined />}
                                loading={batchSwitchingBlock}
                                disabled={!batchBlockId || boardData?.isPastSchoolYear}
                                onClick={handleQuickBatchSwitchBlock}
                                style={{
                                    backgroundColor: '#52c41a',
                                    borderColor: '#52c41a',
                                    fontWeight: 600,
                                }}
                            >
                                Chuyển khối
                            </Button>
                        </Space>
                    </div>
                )}

                <Table<StudentRow>
                    dataSource={filteredStudents}
                    columns={matrixColumns}
                    rowKey="studentClassYearId"
                    loading={loading}
                    pagination={false}
                    size="small"
                    tableLayout="fixed"
                    className="matrix-income-table"
                    scroll={{ x: matrixScrollX, y: 360 }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                        onSelect: (record) => {
                            setSelectedStudentId(record.studentClassYearId);
                        },
                        columnWidth: 40,
                        fixed: 'left',
                    }}
                    rowClassName={(record, index) => {
                        const isFocused = record.studentClassYearId === selectedStudentId;
                        const zebraClass = index % 2 === 0 ? 'matrix-row-even' : 'matrix-row-odd';
                        return isFocused ? `${zebraClass} matrix-row-focused` : zebraClass;
                    }}
                    onRow={(record) => ({
                        onClick: () => setSelectedStudentId(record.studentClassYearId),
                    })}
                />
            </Card>

            {/* VÙNG 3: CHI TIẾT KHOẢN THU CỦA HỌC SINH ĐƯỢC CHỌN */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: '16px 20px' }}
                title={
                    focusedStudent ? (
                        <Space wrap align="center" size={12}>
                            <UserOutlined style={{ color: '#1677ff', fontSize: 16 }} />
                            <Title level={5} style={{ margin: 0 }}>
                                Chi tiết khoản thu của học sinh: <span style={{ color: '#1677ff' }}>{focusedStudent.fullName}</span> ({focusedStudent.studentCode || 'Chưa có mã'})
                            </Title>
                        </Space>
                    ) : (
                        <Title level={5} style={{ margin: 0 }}>Chi tiết khoản thu học sinh</Title>
                    )
                }
                extra={
                    focusedStudent && (
                        <Space size={16} align="center">
                            <Text>
                                Đã đăng ký: <Text strong style={{ color: '#1677ff' }}>{registeredCount}</Text> / {boardData?.columns?.length || 0} khoản
                            </Text>
                            <Divider type="vertical" />
                            <Text>
                                Tổng tiền thu: <Text strong style={{ color: '#cf1322', fontSize: 16 }}>
                                    {focusedStudentTotalMustCollectAmount.toLocaleString('vi-VN')} đ
                                </Text>
                            </Text>
                        </Space>
                    )
                }
            >
                {!focusedStudent ? (
                    <Empty description="Vui lòng chọn một học sinh ở bảng trên để xem chi tiết biểu phí" />
                ) : detailTableData.length === 0 ? (
                    <div>
                        <Empty description={showAllIncomes ? "Lớp học chưa có khoản thu nào được thiết lập." : "Không có khoản thu nào chưa thanh toán hoặc chưa phát sinh trong tháng."} />
                        <div style={{ marginTop: 8, paddingLeft: 4 }}>
                            <Checkbox
                                checked={showAllIncomes}
                                onChange={(e) => setShowAllIncomes(e.target.checked)}
                            >
                                <span style={{ fontSize: 12.5, fontWeight: 500, color: '#595959' }}>
                                    Hiện tất cả nguồn thu
                                </span>
                            </Checkbox>
                        </div>
                    </div>
                ) : (
                    <>
                        <Table<StudentDetailRow>
                            dataSource={detailTableData}
                            columns={zone3Columns}
                            rowKey="incomeSchoolId"
                            pagination={false}
                            size="small"
                            bordered
                            tableLayout="fixed"
                            className="legacy-income-detail-table"
                            scroll={{ x: 1135 }}
                            rowClassName={(record) => (record.isRegistered ? 'registered-row' : 'unregistered-row')}
                        />
                        <div style={{ marginTop: 8, paddingLeft: 4 }}>
                            <Checkbox
                                checked={showAllIncomes}
                                onChange={(e) => setShowAllIncomes(e.target.checked)}
                            >
                                <span style={{ fontSize: 12.5, fontWeight: 500, color: '#595959' }}>
                                    Hiện tất cả nguồn thu
                                </span>
                            </Checkbox>
                        </div>
                    </>
                )}
            </Card>

            {/* MODAL GÁN KHỐI THU CHO CẢ LỚP HOẶC HỌC SINH CHƯA GÁN */}
            <Modal
                title="Gán Khối thu cho học sinh trong lớp"
                open={showAssignBlockModal}
                onCancel={() => setShowAssignBlockModal(false)}
                onOk={handleBatchAssignBlockSubmit}
                confirmLoading={assigningBlock}
                okText="Xác nhận gán"
                cancelText="Hủy"
            >
                <div style={{ marginBottom: 16, marginTop: 12 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>Chọn Khối thu:</Text>
                    <Select
                        placeholder="Chọn khối thu cần áp dụng"
                        style={{ width: '100%' }}
                        value={targetBlockId || undefined}
                        onChange={(val) => setTargetBlockId(val)}
                    >
                        {revenueBlocks.map((rb) => (
                            <Option key={rb.id} value={rb.id}>
                                {rb.name} ({rb.code})
                            </Option>
                        ))}
                    </Select>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>Phạm vi áp dụng:</Text>
                    <Select
                        style={{ width: '100%' }}
                        value={assignTargetType}
                        onChange={(val) => setAssignTargetType(val)}
                    >
                        {selectedRowKeys.length > 0 && (
                            <Option value="selected">
                                Các học sinh đang chọn trên bảng ({selectedRowKeys.length} HS)
                            </Option>
                        )}
                        <Option value="all">Toàn bộ học sinh trong lớp ({boardData?.totalStudents || 0} HS)</Option>
                        <Option value="unassigned">Chỉ các học sinh chưa có khối thu ({boardData?.unassignedBlockCount || 0} HS)</Option>
                    </Select>
                </div>

                <Alert
                    type="info"
                    showIcon
                    message="Lưu ý"
                    description="Khối thu là căn cứ để tự động điền đơn giá và số lượng khi Đăng ký nhanh nguồn thu toàn trường hoặc Lập đăng ký."
                />
            </Modal>

            {/* MODAL XÁC NHẬN TẠO BIÊN LAI NGUỒN THU */}
            <Modal
                title="Tạo biên lai thu tiền (Trạng thái: Chờ duyệt)"
                open={showGenerateInvoiceModal}
                onCancel={() => setShowGenerateInvoiceModal(false)}
                onOk={handleGenerateInvoices}
                confirmLoading={generatingInvoices}
                okText="Xác nhận tạo biên lai"
                cancelText="Hủy"
                okButtonProps={{ style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } }}
            >
                <div style={{ marginTop: 12, marginBottom: 16 }}>
                    <p style={{ marginBottom: 8 }}>
                        Bạn đang chuẩn bị tạo biên lai thu tiền cho:
                    </p>
                    <Alert
                        message={
                            <div>
                                <div>• Lớp: <Text strong>{boardData?.className}</Text></div>
                                <div>• Kỳ làm việc: <Text strong>Tháng {selectedMonth}/{selectedYear?.year()}</Text></div>
                                <div>
                                    • Đối tượng: <Text strong style={{ color: '#1677ff' }}>
                                        {selectedRowKeys.length > 0
                                            ? `${selectedRowKeys.length} học sinh được chọn`
                                            : `Toàn bộ học sinh trong lớp (${boardData?.totalStudents || 0} học sinh)`}
                                    </Text>
                                </div>
                            </div>
                        }
                        type="info"
                        style={{ marginBottom: 12 }}
                    />

                    <Alert
                        message="Quy tắc tạo biên lai"
                        description={
                            <ul style={{ paddingLeft: 18, margin: '4px 0 0 0' }}>
                                <li>Mỗi khoản thu (nguồn thu) được lập thành <strong>1 biên lai riêng biệt</strong> để phụ huynh có thể đóng từng khoản.</li>
                                <li><strong>Thừa tháng trước</strong>: Khấu trừ trực tiếp vào biên lai tháng này (chỉ tạo 1 biên lai với số tiền sau giảm trừ).</li>
                                <li><strong>Nợ tháng trước</strong>: Tách thành <strong>2 biên lai riêng</strong> (1 biên lai tháng này, 1 biên lai nợ tháng trước).</li>
                                <li>Toàn bộ biên lai mới tạo sẽ ở trạng thái <strong>Chờ duyệt (-1)</strong>. Bạn có thể bấm "Hủy nháp" để sửa lại nếu cần.</li>
                            </ul>
                        }
                        type="warning"
                        showIcon
                    />
                </div>
            </Modal>
        </div>
    );
};

export default ClassIncomeBoard;
